import { SyntheticEvent } from 'react';
import styled from '@emotion/styled';

import {
  gray01,
  gray02,
  gray04,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import { fontSize, fontWeight, spacing } from 'presentations/views/styles';
import { ViewerItem } from 'presentations/api/schema/user';
import Link from 'presentations/views/components/atoms/Link';
import SpeedDial from 'presentations/views/components/atoms/SpeedDial';
import Avatar, {
  AvatarBadge,
} from 'presentations/views/components/atoms/Avatar';
import { TeamUserListItem } from 'presentations/api/schema/team_user';
import { transformImageUrl } from 'presentations/assets';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import { Role } from 'domains/models/team_user/role';

interface Props {
  viewer: ViewerItem;
  selectableTeamUsers: TeamUserListItem[];
  selectedTeamUsers: TeamUserListItem[];
  onClickUser?: (user: TeamUserListItem) => void;
  onClickFilter?: (e: SyntheticEvent<HTMLButtonElement>) => void;
  isOpenFilter?: boolean;
}

export default function Component(props: Props) {
  const onClickUser = (e: SyntheticEvent, teamUser: TeamUserListItem) => {
    e.preventDefault();
    props.onClickUser && props.onClickUser(teamUser);
  };

  const renderMember = (teamUser: TeamUserListItem) => {
    const name = teamUser.name ? teamUser.name.substring(0, 1) : '';
    const isActive = props.selectedTeamUsers.some(
      (u) => u.userId === teamUser.userId,
    );
    return (
      <AvatarBadge
        badgeContent={
          isActive ? (
            <CheckIcon
              name="check-solid"
              size="16px"
              styledColor={teamUser.color}
            />
          ) : undefined
        }
      >
        <MenuAvatar
          src={transformImageUrl(teamUser.profileImage, {
            width: 48,
            height: 48,
          })}
          color={teamUser.color}
          variant="rounded"
        >
          {name}
        </MenuAvatar>
      </AvatarBadge>
    );
  };

  return (
    <Container>
      {props.viewer.role >= Role.Restricted ? (
        <UserFilterContainer>
          <MainContent>
            {props.selectableTeamUsers.map((teamUser) => (
              <User
                key={teamUser.userId}
                dataProps={{ 'data-id': teamUser.userId }}
                onClick={(e: SyntheticEvent) => onClickUser(e, teamUser)}
              >
                {renderMember(teamUser)}
                <Name>{teamUser.name}</Name>
              </User>
            ))}
          </MainContent>
        </UserFilterContainer>
      ) : (
        <UserFilterContainer>
          <MainContent>
            {props.selectableTeamUsers.map((teamUser) => (
              <User
                key={teamUser.userId}
                dataProps={{ 'data-id': teamUser.userId }}
                onClick={(e: SyntheticEvent) => onClickUser(e, teamUser)}
              >
                {renderMember(teamUser)}
                <Name>{teamUser.name}</Name>
              </User>
            ))}
          </MainContent>
          {props.onClickFilter && (
            <AddContainer>
              <IconButton onClick={props.onClickFilter}>
                <AddIcon name="account-cog" />
              </IconButton>
              {/* <StyledSpeedDial
                open={props.isOpenFilter || false}
                onClick={props.onClickFilter}
              /> */}
            </AddContainer>
          )}
        </UserFilterContainer>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${gray04};
  flex-shrink: 0;
`;

const UserFilterContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  padding: ${spacing(8)} ${spacing(8)};
  background-color: ${white};
  justify-content: space-between;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  overflow-x: auto;
`;
const AddContainer = styled.div`
  padding-left: ${spacing(8)};
`;

const User = styled(Link)`
  width: 50px;
  height: 52px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;
const MenuAvatar = styled(Avatar)<{ color?: string }>`
  width: 36px;
  height: 36px;
  background-color: ${(props) => props.color};
  font-weight: ${fontWeight('bold')};
`;
const CheckIcon = styled(Icon)<{ styledColor?: string }>`
  color: ${whiteFix};
  background-color: ${(props) => props.styledColor};
  border-radius: 50%;
`;
const Name = styled.div`
  width: 40px;
  overflow: hidden;
  white-space: nowrap;
  font-size: ${fontSize(10)};
  font-weight: ${fontWeight('bold')};
  color: ${gray02};
  text-align: center;
  padding: 0 4px;
  letter-spacing: 0.04em;
`;

const AddIcon = styled(Icon)`
  color: ${gray01};
  width: 24px !important;
  height: 24px !important;
`;
